/**
 * CharacterPawn - A ready-to-use pawn with camera, animation, and movement.
 * Provides complete third-person character setup with default model and collision.
 */

import * as THREE from 'three';

import { BasePawnMovementComponent } from '../components/movement/BasePawnMovementComponent.js';
import { CharacterMovementComponent } from '../components/movement/CharacterMovementComponent.js';
import { SpringArmComponent } from '../components/movement/SpringArmComponent.js';
import { SceneComponent } from '../components/SceneComponent.js';
import { AnimationStateMachineComponent } from '../components/visual/AnimationStateMachineComponent.js';
import { MeshComponent } from '../components/visual/MeshComponent.js';
import { ModelMeshComponent } from '../components/visual/ModelMeshComponent.js';
import {
  CAMERA_FAR,
  CAMERA_FOV,
  CAMERA_NEAR,
  CHARACTER_HEIGHT,
  CHARACTER_WIDTH,
  DEFAULT_CHARACTER_ANIMATION_CONFIG_URL,
  DEFAULT_CHARACTER_MODEL_URL
} from '../const.js';
import { PhysicsMotionType } from '../physics/PhysicsTypes.js';
import { EngineClass } from '../systems/ClassRegistry.js';
import { DefaultCollisionProfile } from '../systems/collision/CollisionTypes.js';
import { Color, MathHelpers } from '../utils/index.js';
import { property } from '../utils/serialization/decorator.js';

import { Pawn, type PawnOptions } from './Pawn.js';

export type CharacterPawnOptions = Omit<PawnOptions, 'rootComponent' | 'movementComponent'>;

/**
 * This is a more opinionated pawn that provides a common setup for a pawn with camera, animation, and movement.
 * You can either use it as the base class, override the various setupXXX methods for customization,
 * or use it as reference implementation to create your own class from scratch.
 *
 * The default implementation setups a humanoid pawn (using engine assets) with a third person camera.
 */
@EngineClass('CharacterPawn')
export class CharacterPawn extends Pawn {
  @property({ hidden: true })
  protected camera!: THREE.Camera;

  // Camera pivot for pitch control
  @property({ hidden: true })
  protected cameraPivot: SceneComponent | null = null;

  // Spring arm component for camera distance control
  @property({ hidden: true })
  protected springArm: SpringArmComponent | null = null;

  // optional, could be null if the visual is the root component
  @property({ hidden: true })
  protected visualComponent: SceneComponent | null = null;

  // optional, null if the pawn requires no animation
  @property({ hidden: true })
  protected animationComponent: AnimationStateMachineComponent | null = null;

  // Camera distance constraints (in meters)
  @property({ type: 'number', min: 0.5, max: 10, step: 0.1, category: 'Camera' })
  public cameraMinDistance: number = 1;

  @property({ type: 'number', min: 1, max: 20, step: 0.1, category: 'Camera' })
  public cameraMaxDistance: number = 10;

  @property({ type: 'number', min: 0.1, max: 2, step: 0.1, category: 'Camera' })
  public cameraZoomSensitivity: number = 0.5;

  @property({ type: 'number', min: 0, max: 0.5, step: 0.01, category: 'Camera' })
  public cameraCollisionBuffer: number = 0.05;

  @property({ type: 'number', min: 0.1, max: 20, step: 0.5, category: 'Camera' })
  public cameraSpringStiffness: number = 4.5;

  @property({ type: 'number', min: -90, max: 0, step: 1, category: 'Camera' })
  public cameraMinPitchDegrees: number = -80;

  @property({ type: 'number', min: 0, max: 90, step: 1, category: 'Camera' })
  public cameraMaxPitchDegrees: number = 80;

  /**
   * Initializes the pawn.
   * Do NOT pass the root and movement components since they are created internally.
   */
  public override initialize(options?: CharacterPawnOptions): void {
    super.initialize({
      ...options,
      rootComponent: this.createRootComponent(),
      movementComponent: this.createMovementComponent(),
    });

    // the various setup calls should attach the components to the actor
    // this base class makes no assumptions about the attachment logic
    this.camera = this.setupCamera();
    this.animationComponent = this.setupAnimationComponent();
    this.visualComponent = this.setupVisualComponent();

    // sanity check to make sure everything is attached
    if (!this.camera.parent) {
      throw new Error('Camera not attached to actor');
    }

    if (this.animationComponent && !this.animationComponent.parent) {
      throw new Error('Animation component not attached to actor');
    }

    if (this.visualComponent && !this.visualComponent.parent) {
      throw new Error('Visual component not attached to actor');
    }
  }

  protected createRootComponent(): SceneComponent {
    // create the root component for collision
    const radius = CHARACTER_WIDTH / 2;
    return MeshComponent.create({
      name: 'RootComponent',
      geometry: new THREE.CapsuleGeometry(radius, CHARACTER_HEIGHT - radius * 2),
      material: new THREE.MeshStandardMaterial({
        color: Color.YELLOW,
        visible: false,
      }),
      physicsOptions: {
        enabled: true,
        motionType: PhysicsMotionType.KinematicPositionBased,
        collisionProfile: DefaultCollisionProfile.Character,
      },
    });
  }

  protected createMovementComponent(): BasePawnMovementComponent {
    return CharacterMovementComponent.create({
      name: 'MovementComponent',
    });
  }

  /**
   * Returns the initial pivot and camera positions in Actor-Local space.
   * If their positions are the same then the camera is attached to the pivot directly (first person style).
   * Otherwise the camera is attached to the pivot through a spring arm (third person style).
   */
  protected getInitialCameraPositions(): {
    pivotPosition: THREE.Vector3;
    cameraPosition: THREE.Vector3;
    } {
    const pivotPosition = new THREE.Vector3(0, 0, 0);
    const cameraPosition = new THREE.Vector3(
      pivotPosition.x,
      pivotPosition.y + CHARACTER_HEIGHT * 1.3,
      pivotPosition.z + CHARACTER_HEIGHT * 2
    );
    return { pivotPosition, cameraPosition };
  }

  /**
   * Creates AND attaches the camera to the actor scene graph.
   * The default setup models a hierarchy like this: root -> pivot -> spring arm (optional) -> camera.
   * In a typical first/third person setup, the position and yaw come from the root component (driven by the movement component) and the pivot provides the pitch.
   */
  protected setupCamera(): THREE.Camera {
    const camera = new THREE.PerspectiveCamera(CAMERA_FOV, 1, CAMERA_NEAR, CAMERA_FAR);
    camera.name = 'Camera';

    // Get initial camera positions
    const { pivotPosition, cameraPosition } = this.getInitialCameraPositions();

    // Create pivot at configured position
    this.cameraPivot = SceneComponent.create({
      name: 'CameraPivot',
      position: pivotPosition,
    });

    const lookAtVector = cameraPosition.clone().sub(pivotPosition);
    const armLength = lookAtVector.length();
    if (armLength > 0) {
      // Third-person camera: follows the character with a spring arm that collides with world geometry.
      // Pivot controls pitch (looking up/down), spring arm maintains distance, camera orbits around the character.
      const pitch = -Math.atan2(lookAtVector.y, lookAtVector.z);
      // Create spring arm with calculated settings
      this.springArm = SpringArmComponent.create({
        name: 'CameraSpringArm',
        armLength,
        collisionBuffer: this.cameraCollisionBuffer,
        springBackLambda: this.cameraSpringStiffness,
      });
      // Set default max camera distance based on initial value
      this.cameraMaxDistance = Math.max(this.cameraMaxDistance, armLength * 2);
      // Build hierarchy: root -> pivot -> spring arm -> camera
      this.rootComponent.add(this.cameraPivot);
      this.cameraPivot.add(this.springArm);
      this.springArm.add(camera);
      // Apply initial pitch to position camera above and behind
      this.cameraPivot.rotation.x = pitch;
    } else {
      // First-person camera: camera and pivot are at the same position.
      // Pivot provides pitch control for looking up/down.
      this.rootComponent.add(this.cameraPivot);
      this.cameraPivot.add(camera);
    }

    return camera;
  }

  /**
   * Creates AND attaches the animation component to the actor scene graph.
   */
  protected setupAnimationComponent(): AnimationStateMachineComponent | null {
    // animation component for the humanoid model
    const animationComponent = AnimationStateMachineComponent.create({
      name: 'AnimationComponent',
      configUrl: DEFAULT_CHARACTER_ANIMATION_CONFIG_URL
    });
    this.rootComponent.add(animationComponent);
    return animationComponent;
  }

  /**
   * Creates AND attaches the visual component to the actor scene graph.
   */
  protected setupVisualComponent(): SceneComponent | null {
    // create the mesh component and deal with the different pivot and rotation in the model
    const meshComponent =  ModelMeshComponent.create({
      name: 'MeshComponent',
      modelUrl: DEFAULT_CHARACTER_MODEL_URL,
      scale: new THREE.Vector3(1, 1, 1),
      rotation: new THREE.Euler(0, Math.PI, 0),
      position: new THREE.Vector3(0, -CHARACTER_HEIGHT / 2, 0),
      physicsOptions: {
        // disable physics for the visual mesh
        enabled: false,
      },
      castShadow: true,
    });
    this.rootComponent.add(meshComponent);
    return meshComponent;
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);
    this.updateCamera(deltaTime);

    this.animationComponent ??= this.getComponent(AnimationStateMachineComponent);
    if (!this.animationComponent || !this.animationComponent.isReady()) {
      return;
    }

    const params = this.getAnimationParameters();
    this.animationComponent.setParameter(params);
  }

  /**
   * Updates the camera pivot pitch based on look up input.
   * Allows the player to look up/down.
   */
  protected updateCamera(deltaTime: number): void {
    const pitchingComponent = this.cameraPivot;
    if (!pitchingComponent ) return;

    const movementComponent = this.movementComponent;
    if (!(movementComponent instanceof CharacterMovementComponent)) return;

    const inputState = movementComponent.getInputState();
    if (inputState.lookUpInput === 0) return;

    // Get look speed from movement component
    const lookSpeed = movementComponent.lookUpSpeed;

    // Calculate pitch delta
    const pitchDelta = inputState.lookUpInput * lookSpeed;

    const forwardY = new THREE.Vector3(0, 0, -1).applyQuaternion(pitchingComponent.quaternion).y;
    const currentPitch = Math.asin(THREE.MathUtils.clamp(forwardY, -1, 1));

    // Update pitch with clamping to prevent over-rotation
    const minPitchRad = THREE.MathUtils.degToRad(this.cameraMinPitchDegrees);
    const maxPitchRad = THREE.MathUtils.degToRad(this.cameraMaxPitchDegrees);
    const newPitch = THREE.MathUtils.clamp(
      currentPitch + pitchDelta,
      minPitchRad,
      maxPitchRad
    );

    pitchingComponent.addLocalRotation(MathHelpers.makeRotation({ pitch: newPitch - currentPitch }));
  }

  /**
   * Handles mouse wheel events to adjust spring arm length.
   * Positive deltaY (scrolling down) zooms out, negative zooms in.
   * @param e - The wheel event
   * @returns True if the event was handled
   */
  public override handleMouseWheel(e: WheelEvent): boolean {
    if (!this.springArm) return false;

    // Prevent default to stop page scrolling when focused on the game
    e.preventDefault();

    // Get current arm length
    const currentLength = this.springArm.armLength!;

    // Calculate zoom delta (normalize wheel direction)
    // e.deltaY > 0 means scrolling down (zoom out), < 0 means scrolling up (zoom in)
    const zoomDelta = Math.sign(e.deltaY) * this.cameraZoomSensitivity;

    // Apply new camera distance with clamping
    const newLength = THREE.MathUtils.clamp(
      currentLength + zoomDelta,
      this.cameraMinDistance,
      this.cameraMaxDistance
    );

    this.springArm.armLength = newLength;
    return true;
  }

  protected getAnimationParameters(): Record<string, any> {
    const movementComponent = this.movementComponent;
    if (movementComponent instanceof CharacterMovementComponent) {
      const { forward, right } = movementComponent.getVelocities();
      const grounded = movementComponent.getGrounded?.() ?? true;
      let planarSpeed = Math.sqrt(forward * forward + right * right);
      if (planarSpeed < 0.01) planarSpeed = 0;

      // Only set parameters present in mannequin.anim.json
      const params: Record<string, any> = {
        isRunning: planarSpeed > 2,
        isJumping: !grounded,
        forward: Math.max(0, forward),
        back: Math.max(0, -forward),
        right: Math.max(0, right),
        left: Math.max(0, -right),
      };
      if (planarSpeed === 0) {
        params.forward = 0;
        params.back = 0;
      }
      return params;
    }
    return {};
  }
}
